import {useQueryClient} from "@tanstack/react-query";
import React, {useEffect, useRef, useState} from 'react';
import {ElementTypeDto} from "../../../../../common/apis/Elements";
import {ReferenceDefinitionCreateDto, RelationshipDirection} from "../../../../../common/apis/stereotype/ReferenceDefinitionDto";
import {StereotypeApiClient} from "../../../../../common/apis/stereotype/StereotypeApiClient";
import {StereotypeDto} from "../../../../../common/apis/stereotype/StereotypeDto";
import {ArchimateRelationship, ArchimateRelationshipType} from "../../../../../common/archimate/ArchimateRelationship";
import {ValidationError} from "../../../../../common/ValidationError";
import {Button} from "../../../../../components/button/Button";
import {ButtonLayout, NegativeButtons, PositiveButtons} from "../../../../../components/button/ButtonLayout";
import {CancelButton} from "../../../../../components/button/CancelButton";
import Dialog from '../../../../../components/dialogs/Dialog';
import DialogActions from "../../../../../components/dialogs/DialogActions";
import DialogContent from '../../../../../components/dialogs/DialogContent';
import DialogTitle from '../../../../../components/dialogs/DialogTitle';
import Grid from '../../../../../components/dialogs/Grid';
import Checkbox from "../../../../../components/fields/Checkbox";
import SingleSelectField from "../../../../../components/fields/select/SingleSelectField";
import TextField from '../../../../../components/fields/textfield/TextField';
import SectionHeader from "../../../../../components/section/SectionHeader";
import {_transl, t} from '../../../../../store/localization/TranslMessasge';
import {CommonTranslationKey} from '../../CommonTranslationKey';
import {useFindAllElementTypesQuery} from "../../elements/ElementQuery";
import {ElementTranslationKey} from "../../elements/ElementTranslationKey";
import ElementTypeSingleSelectField from "../../elements/ElementTypeSingleSelectField";
import {ErrorTranslationKey} from "../../ErrorTranslationKey";
import Snackbar from "../../snackbar/Snackbar";
import {StereotypeErrorCode} from "../StereotypeErrorCodes";
import {invalidateFindAllStereotypesQueryCache} from "../StereotypeQuery";
import {StereotypesTranslationKey} from "../StereotypesTranslationKey";
import {ReferenceRelationshipDirectionFormatter} from "./ReferenceRelationshipDirectionFormatter";

const DEFAULT_RELATIONSHIP_TYPE = ArchimateRelationshipType.ASSOCIATION;

function defaultReferenceDef(): Partial<ReferenceDefinitionCreateDto> {
    return {
        name: "",
        description: "",
        relationshipType: ArchimateRelationship.valueOf(DEFAULT_RELATIONSHIP_TYPE)?.standardNames[0],
        relationshipDirection: RelationshipDirection.OUTGOING,
        relatedElementFilter: {
            type: ""
        },
        required: false,
        multiple: false,
    };
}

interface ReferenceDefinitionCreateDialogProps {
    open: boolean;
    stereotypeId: string;
    stereotypes: StereotypeDto[];
    onCreated: (referenceDefinitionId: string) => void;
    onClose: () => void;
}

export function ReferenceDefinitionCreateDialog(props: Readonly<ReferenceDefinitionCreateDialogProps>) {
    const { open, stereotypeId, stereotypes, onCreated, onClose } = props;
    const stereotypeApiClient = useRef(new StereotypeApiClient());

    const [referenceDef, setReferenceDef] = useState<Partial<ReferenceDefinitionCreateDto>>(defaultReferenceDef());
    const [relationshipType, setRelationshipType] = useState<ArchimateRelationship | null>(ArchimateRelationship.valueOf(DEFAULT_RELATIONSHIP_TYPE));
    const [elementType, setElementType] = useState<ElementTypeDto | null>(null);
    const [stereotype, setStereotype] = useState<StereotypeDto | null>(null);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [inProgress, setInProgress] = useState(false);

    useEffect(() => {
        return () => {
            if (open) {
                setReferenceDef(defaultReferenceDef());
                setRelationshipType(ArchimateRelationship.valueOf(DEFAULT_RELATIONSHIP_TYPE));
                setErrors({});
            }
        }
    }, [open]);

    const queryClient = useQueryClient();
    const {data: elementTypes = []} = useFindAllElementTypesQuery();

    function updateElementType(type: ElementTypeDto | null) {
        setElementType(type);
        setStereotype(null);
    }

    function createReferenceDefinition() {
        if (!validateForm()) {
            return;
        }

        setInProgress(true);

        const referenceDefCreate: ReferenceDefinitionCreateDto = {
            name: referenceDef.name!.trim(),
            description: referenceDef.description?.trim(),
            relationshipType: referenceDef.relationshipType!,
            relationshipDirection: referenceDef.relationshipDirection!,
            required: referenceDef.required ?? false,
            multiple: referenceDef.multiple ?? false,
            relatedElementFilter: {
                type: elementType!.name,
                stereotypeId: stereotype?.id
            },
        };

        stereotypeApiClient.current.createReference(stereotypeId, referenceDefCreate)
                .then((definitionId) => {
                    onCreated(definitionId);
                    onClose();
                    invalidateFindAllStereotypesQueryCache(queryClient);
                    Snackbar.success(t(StereotypesTranslationKey.ATTRIBUTE_CREATED_SUCCESSFULLY));
                })
                .catch((error) => {
                    if (error instanceof ValidationError && error.error.code === StereotypeErrorCode.CONFLICTING_NAME) {
                        setErrors({name: t(StereotypesTranslationKey.ATTRIBUTE_WITH_SAME_NAME_ALREADY_EXISTS)});
                    } else {
                        Snackbar.error(t(ErrorTranslationKey.UNEXPECTED_ERROR_OCCURRED), error);
                    }
                })
                .finally(() => setInProgress(false));
    }

    function validateForm(): boolean {
        const newErrors: Record<string, string> = {};
        if (!referenceDef.name?.trim()) {
            newErrors.name = t(CommonTranslationKey.FILL_OUT_THIS_FIELD);
        }

        if (!referenceDef.relationshipType) {
            newErrors.relationshipType = t(CommonTranslationKey.FILL_OUT_THIS_FIELD);
        }

        if (!referenceDef.relationshipDirection) {
            newErrors.relationshipDirection = t(CommonTranslationKey.FILL_OUT_THIS_FIELD);
        }

        if (!elementType) {
            newErrors.elementType = t(CommonTranslationKey.FILL_OUT_THIS_FIELD);
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }

    const directionOptions = Object.keys(RelationshipDirection) as RelationshipDirection[];
    const relationshipTypeOptions = ArchimateRelationship.values();
    const stereotypeOptions: StereotypeDto[] = [];
    if (elementType && stereotypes) {
        stereotypeOptions.push(...stereotypes.filter(stereotype => stereotype.elementType === elementType.name));
    }

    return (
            <>
                <Dialog open={open} onClose={onClose} maxWidth="md">
                    <DialogTitle title={t(StereotypesTranslationKey.NEW_SMART_REFERENCE)}
                            onDialogClosed={onClose} />
                    <DialogContent>
                        <Grid container>
                            <Grid item xs={12}>
                                <SectionHeader title={t(StereotypesTranslationKey.DETAILS)}/>
                            </Grid>
                            <Grid item xs={12} sm={12}>
                                <TextField
                                        label={t(StereotypesTranslationKey.NAME)}
                                        value={referenceDef.name}
                                        onChange={value => setReferenceDef({...referenceDef, name: value})}
                                        errorMessage={errors.name}
                                        required={true}/>
                            </Grid>
                            <Grid item xs={12} sm={12} md={5}>
                                <SingleSelectField
                                        label={t(StereotypesTranslationKey.RELATIONSHIP_TYPE)}
                                        value={relationshipType}
                                        onChange={value => setRelationshipType(value as ArchimateRelationship | null)}
                                        options={relationshipTypeOptions}
                                        getOptionLabel={(option) => option.visibleName}
                                        errorMessage={errors.relationshipType}
                                        required={true}/>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <SingleSelectField
                                        label={t(StereotypesTranslationKey.RELATIONSHIP_DIRECTION)}
                                        value={referenceDef.relationshipDirection}
                                        onChange={value => setReferenceDef({...referenceDef, relationshipDirection: value as RelationshipDirection})}
                                        options={directionOptions}
                                        getOptionLabel={(option) => ReferenceRelationshipDirectionFormatter.formatDirection(option, t)}
                                        errorMessage={errors.relationshipDirection}
                                        required={true}/>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Checkbox
                                        label={t(StereotypesTranslationKey.MULTIPLE)}
                                        checked={referenceDef.multiple}
                                        onChange={value => setReferenceDef({...referenceDef, multiple: value})}/>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                        label={t(CommonTranslationKey.DESCRIPTION)}
                                        value={referenceDef.description ?? ''}
                                        onChange={value => setReferenceDef({...referenceDef, description: value})}
                                        multiline
                                        rows={4}/>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Checkbox
                                        label={t(StereotypesTranslationKey.SMART_REFERENCE_REQUIRED)}
                                        checked={referenceDef.required}
                                        onChange={value => setReferenceDef({...referenceDef, required: value})}/>
                            </Grid>

                            <Grid item xs={12}>
                                <SectionHeader title={t(StereotypesTranslationKey.ELEMENT_FILTER)} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <ElementTypeSingleSelectField
                                        label={_transl(ElementTranslationKey.DIALOG_CREATE_ELEMENT_FIELD_TYPE)}
                                        options={elementTypes}
                                        value={elementType}
                                        onChange={updateElementType}
                                        errorMessage={errors.elementType}/>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <SingleSelectField
                                        id={"stereotype"}
                                        label={_transl(StereotypesTranslationKey.STEREOTYPE)}
                                        value={stereotype}
                                        onChange={setStereotype}
                                        options={stereotypeOptions}
                                        getOptionLabel={(stereotype) => stereotype.name}
                                        disabled={stereotypeOptions.length === 0}/>
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <ButtonLayout>
                            <PositiveButtons>
                                <Button label={t(CommonTranslationKey.CREATE)}
                                        variant="contained"
                                        onClick={createReferenceDefinition}
                                        disabled={inProgress} />
                            </PositiveButtons>
                            <NegativeButtons>
                                <CancelButton onClick={onClose} disabled={inProgress} />
                            </NegativeButtons>
                        </ButtonLayout>
                    </DialogActions>
                </Dialog>
            </>
    );
}